import React, { ReactNode, useLayoutEffect, useState } from "react";
import ClientsList from "common/Lists/ClientsList";
import PairsList from "common/Lists/PairsList";
import OrderInfo from "features/portfolio/CreateOrder/OrderInfo/OrderInfo";
import SymbolsList from "common/Lists/SymbolsList";
import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined";
import Modal from "common/Modal/Modal";
import Button from "common/Buttons/Button";
import Steps from "common/Steps/Steps";
import {
  ConsolidatedCoinData,
  LanguageNameSpaces,
  SymbolClientsData,
  UsageType,
} from "@shared/types";
import { getAssetFromSymbol } from "@shared/utils/misc";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import Drawer from "common/Drawers/Drawer.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import styled, { useTheme } from "styled-components";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { MOBILE_SEARCH } from "common/constants.ts";
import { StepItem } from "common/Steps/types.ts";
import useQuotaModal from "features/settings/Quota/useQuotaModal.tsx";

interface CreateOrderModalProps {
  coinData?: ConsolidatedCoinData;
  closeModal: () => void;
  isContract?: boolean;
  isOpen?: boolean;
}

// When coinData is not passed in, modal is used to create an entry order
// (as opposed to a take-profit order on an existing coin or contract)
const CreateOrderModal = ({
  coinData,
  closeModal,
  isContract,
  isOpen,
}: CreateOrderModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const theme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isSmallMobile = useMedia([`(max-width: 390px)`], [true], false);
  const coinOrSymbol = coinData?.symbol;
  const isEntryOrder = !coinOrSymbol;
  const [selectedSymbol, setSelectedSymbol] = useState(coinOrSymbol);
  const [selectedAccount, setSelectedAccount] = useState<
    SymbolClientsData | null | undefined
  >(null);
  const shouldShowPairSelection = coinOrSymbol
    ? coinOrSymbol.split("/").length === 1
    : false;
  const [step, setStep] = useState(0);
  const [okButton, setOkButton] = useState<ReactNode | null>(null);
  const {
    isLoading: quotaLoading,
    handleTriggerClick,
    quotaModal,
  } = useQuotaModal({
    usageType: UsageType.MANUAL_TRADE,
  });

  useLayoutEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep(0);
        setOkButton(null);
        setSelectedAccount(null);
      }, 1000);
    } else {
      setSelectedSymbol(coinOrSymbol);
    }
  }, [isOpen]);

  const moveToNextStep = () => {
    setStep((step) => step + 1);
  };

  const moveToStep = (updatedStep: number) => {
    setStep(updatedStep);

    if (
      shouldShowPairSelection &&
      step === 2 &&
      updatedStep < 2 &&
      selectedSymbol
    ) {
      setSelectedSymbol(getAssetFromSymbol(selectedSymbol));
    }
    setOkButton(null);
  };

  const moveToPreviousStep = () => {
    if (step > 0) {
      moveToStep(step > 0 ? step - 1 : step);
    }
  };

  const handleSelectAccount = (client?: SymbolClientsData) => {
    setSelectedAccount(client);
    moveToNextStep();
  };

  const handleSelectPair = (pair: string) => {
    setSelectedSymbol(coinOrSymbol + "/" + pair);
    moveToNextStep();
  };

  const handleSelectSymbol = (symbol: string) => {
    setSelectedSymbol(symbol);
    moveToNextStep();
  };

  const getStepsAndComponents = (): [StepItem[], Record<number, ReactNode>] => {
    const stepItems: StepItem[] = [];
    const stepNumToContentComponents: Record<number, ReactNode> = {};

    const addStepAndComponent = (step: StepItem, component: ReactNode) => {
      stepNumToContentComponents[stepItems.length] = component;
      stepItems.push(step);
    };

    if (isEntryOrder) {
      addStepAndComponent(
        {
          title: `${isMobile ? "" : t("create_order.choose") + " "}${t(
            "create_order.symbol"
          )}`,
        },
        <SymbolsList handleSelectSymbol={handleSelectSymbol} />
      );
    }
    if (!isContract) {
      addStepAndComponent(
        {
          title: `${isMobile ? "" : t("create_order.choose") + " "}${t(
            "create_order.account"
          )}`,
        },
        <ClientsList
          coinOrSymbol={selectedSymbol}
          handleSelectAccount={handleSelectAccount}
          isEntry={isEntryOrder}
        />
      );
    }
    if (shouldShowPairSelection) {
      addStepAndComponent(
        {
          title: `${isBottomNavigator ? "" : t("create_order.choose") + " "}${t(
            "create_order.pair"
          )}`,
        },
        <PairsList
          coin={coinOrSymbol}
          accountId={selectedAccount && selectedAccount.id}
          handleSelectPair={handleSelectPair}
        />
      );
    }
    addStepAndComponent(
      { title: t("create_order.modal_title") },
      <OrderInfo
        account={selectedAccount}
        symbol={selectedSymbol}
        isEntry={isEntryOrder}
        setOkButton={setOkButton}
        moveToPreviousStep={moveToPreviousStep}
        closeModal={closeModal}
        isContract={isContract}
        isReverse={coinData?.is_short}
        accountId={coinData?.client_id}
        marginType={coinData?.margin_type}
        quotaLoading={quotaLoading}
        handleTriggerClick={handleTriggerClick}
      />
    );
    return [stepItems, stepNumToContentComponents];
  };

  const onStepChange = (updatedStep: number) => {
    if (updatedStep < step) {
      moveToStep(updatedStep);
    }
  };

  const [stepItems, stepNumToContentComponents] = getStepsAndComponents();

  const showPreviousButton = step > 0;
  const previousButton = showPreviousButton ? (
    <Button icon={<ArrowLeftOutlined />} onClick={moveToPreviousStep} />
  ) : (
    <span />
  );
  const footer = (showPreviousButton || okButton) && (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {previousButton}
      <div>{okButton}</div>
    </div>
  );

  const modalTitle = isEntryOrder ? t("buttons.buy") : t("create_order.sell");
  const showSteps = stepItems.length > 1;
  const content = (
    <>
      {showSteps && (
        <Steps
          current={step}
          style={{
            marginBottom: "24px",
            padding: isSmallMobile ? 0 : "0 16px",
          }}
          responsive={false}
          size="small"
          titlePlacement={isMobile ? "vertical" : undefined}
          items={stepItems}
          onChange={onStepChange}
        />
      )}
      <StepWrapper $isBottomNavigator={isBottomNavigator}>
        {stepNumToContentComponents[step]}
      </StepWrapper>
    </>
  );

  if (isBottomNavigator) {
    return (
      <StyledDrawer open={isOpen} onClose={closeModal} destroyOnHidden={true}>
        {showPreviousButton && (
          <BackWrapper onClick={moveToPreviousStep}>
            <ArrowLeftOutlined
              style={{ marginRight: 8, color: theme.colors.primaryText }}
            />
          </BackWrapper>
        )}
        {content}
        <CreateButtonWrapper>{okButton}</CreateButtonWrapper>
      </StyledDrawer>
    );
  }

  return (
    <>
      <Modal
        onCancel={closeModal}
        footer={footer}
        open={isOpen}
        title={modalTitle}
        styles={{
          body: {
            padding: "16px 0",
            overflow: "hidden",
          },
        }}
        width={550}
        destroyOnHidden={true}
      >
        {content}
      </Modal>
      {quotaModal}
    </>
  );
};

export default CreateOrderModal;

const BackWrapper = styled.div`
  position: absolute;
  left: 8px;
  top: 5px;
  font-size: 16px;
  padding: 8px;
`;
const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 46px 0 0 0 !important;
    overflow: hidden !important;
    display: flex;
    flex-direction: column;
  }
`;
const CreateButtonWrapper = styled.div`
  padding: 16px;
`;

const StepWrapper = styled.div<{
  $isBottomNavigator?: boolean;
}>`
  display: flex;
  justify-content: center;
  ${(p) =>
    p.$isBottomNavigator
      ? `
    flex: 1;
    overflow: hidden;
  `
      : `
    height: 500px;
    max-height: 60vh;
  `};
`;
